import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Plus, FileText, Image, Settings, BarChart, BookOpen, Eye, Layout } from 'lucide-react';
import { collection, query, orderBy, limit, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import BrandLogo from '../components/BrandLogo';
import { useBrandSettings } from '../contexts/BrandSettingsContext';

const CMSDashboard = () => {
  const { brandName } = useBrandSettings();
  const [stats, setStats] = useState({
    pages: 0,
    posts: 0,
    media: 0,
    publishedPages: 0,
    publishedPosts: 0,
    draftPages: 0,
    draftPosts: 0
  });
  const [recentPages, setRecentPages] = useState([]);
  const [recentPosts, setRecentPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);

      const pagesSnapshot = await getDocs(collection(db, 'cms_pages'));
      const postsSnapshot = await getDocs(collection(db, 'cms_blog_posts'));
      const mediaSnapshot = await getDocs(collection(db, 'cms_media_library'));

      const pages = pagesSnapshot.docs.map(doc => doc.data());
      const posts = postsSnapshot.docs.map(doc => doc.data());

      setStats({
        pages: pagesSnapshot.size,
        posts: postsSnapshot.size,
        media: mediaSnapshot.size,
        publishedPages: pages.filter(p => p.status === 'published').length,
        publishedPosts: posts.filter(p => p.status === 'published').length,
        draftPages: pages.filter(p => p.status === 'draft').length,
        draftPosts: posts.filter(p => p.status === 'draft').length
      });

      const pagesQuery = query(
        collection(db, 'cms_pages'),
        orderBy('updatedAt', 'desc'),
        limit(5)
      );
      const pagesData = await getDocs(pagesQuery);
      setRecentPages(pagesData.docs.map(doc => ({ id: doc.id, ...doc.data() })));

      const postsQuery = query(
        collection(db, 'cms_blog_posts'),
        orderBy('createdAt', 'desc'),
        limit(5)
      );
      const postsData = await getDocs(postsQuery);
      setRecentPosts(postsData.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    const d = date.toDate ? date.toDate() : new Date(date);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Top Navigation Bar */}
      <div className="bg-card border-b border-border sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <BrandLogo width={120} height={32} />
            <span className="text-sm font-medium text-muted-foreground">CMS</span>
          </div>
          <nav className="flex items-center gap-1">
            <Link to="/cms">
              <Button variant="ghost" size="sm" className="gap-2">
                <BarChart className="h-4 w-4" />
                Dashboard
              </Button>
            </Link>
            <Link to="/cms/pages">
              <Button variant="ghost" size="sm" className="gap-2">
                <FileText className="h-4 w-4" />
                Pages
              </Button>
            </Link>
            <Link to="/cms/blog">
              <Button variant="ghost" size="sm" className="gap-2">
                <BookOpen className="h-4 w-4" />
                Blog
              </Button>
            </Link>
            <Link to="/cms/system-pages">
              <Button variant="ghost" size="sm" className="gap-2">
                <Layout className="h-4 w-4" />
                System Pages
              </Button>
            </Link>
            <Link to="/cms/media">
              <Button variant="ghost" size="sm" className="gap-2">
                <Image className="h-4 w-4" />
                Media
              </Button>
            </Link>
            <Link to="/cms/settings">
              <Button variant="ghost" size="sm" className="gap-2">
                <Settings className="h-4 w-4" />
                Settings
              </Button>
            </Link>
          </nav>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Dashboard</h1>
            <p className="text-muted-foreground mt-1">Welcome to your content management system</p>
          </div>
          <Link to="/" target="_blank">
            <Button variant="outline" className="gap-2">
              <Eye className="h-4 w-4" />
              View Site
            </Button>
          </Link>
        </div>

        {/* Stats Grid */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Pages</CardTitle>
              <FileText className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.pages}</div>
              <p className="text-xs text-muted-foreground">
                {stats.publishedPages} published, {stats.draftPages} drafts
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Blog Posts</CardTitle>
              <BookOpen className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.posts}</div>
              <p className="text-xs text-muted-foreground">
                {stats.publishedPosts} published, {stats.draftPosts} drafts
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Media Files</CardTitle>
              <Image className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.media}</div>
              <p className="text-xs text-muted-foreground">
                Images, videos, documents
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Quick Actions</CardTitle>
              <Plus className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent className="space-y-2">
              <Link to="/cms/pages/new">
                <Button variant="outline" size="sm" className="w-full justify-start">
                  <Plus className="h-3 w-3 mr-2" />
                  New Page
                </Button>
              </Link>
              <Link to="/cms/system-pages">
                <Button variant="outline" size="sm" className="w-full justify-start">
                  <Layout className="h-3 w-3 mr-2" />
                  Edit System Pages
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>

        {/* Recent Activity */}
        <div className="grid gap-6 md:grid-cols-2">
          {/* Recent Pages */}
          <Card>
            <CardHeader>
              <CardTitle>Recent Pages</CardTitle>
              <CardDescription>Latest updated pages</CardDescription>
            </CardHeader>
            <CardContent>
              {recentPages.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">No pages yet</p>
              ) : (
                <div className="space-y-3">
                  {recentPages.map((page) => (
                    <Link
                      key={page.id}
                      to={`/cms/pages/${page.id}`}
                      className="flex items-center justify-between p-3 rounded-lg hover:bg-muted transition-colors"
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{page.title || 'Untitled'}</p>
                        <p className="text-xs text-muted-foreground">
                          Updated {formatDate(page.updatedAt)}
                        </p>
                      </div>
                      <Badge variant={page.status === 'published' ? 'default' : 'secondary'}>
                        {page.status}
                      </Badge>
                    </Link>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Recent Posts */}
          <Card>
            <CardHeader>
              <CardTitle>Recent Blog Posts</CardTitle>
              <CardDescription>Latest blog posts</CardDescription>
            </CardHeader>
            <CardContent>
              {recentPosts.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">No posts yet</p>
              ) : (
                <div className="space-y-3">
                  {recentPosts.map((post) => (
                    <Link
                      key={post.id}
                      to={`/cms/blog/${post.id}`}
                      className="flex items-center justify-between p-3 rounded-lg hover:bg-muted transition-colors"
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{post.title || 'Untitled'}</p>
                        <p className="text-xs text-muted-foreground">
                          {formatDate(post.createdAt)}
                        </p>
                      </div>
                      <Badge variant={post.status === 'published' ? 'default' : 'secondary'}>
                        {post.status}
                      </Badge>
                    </Link>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default CMSDashboard;
